import React, { useState, useEffect, useMemo } from 'react';
import { localDate } from '../utils/dates';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Chip, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Snackbar, Alert, Autocomplete, LinearProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, FactCheck as HoursIcon } from '@mui/icons-material';
import { clinicalAPI, academicAPI, teacherAPI } from '../services/api';

const DEPARTMENTS = [
  'Skill Lab (simulation)', 'Medical Ward', 'Surgical Ward', 'Paediatric Ward', 'Labour Room / OBG', 'Postnatal Ward',
  'Psychiatry', 'Community Health Centre', 'ICU', 'Emergency / Casualty', 'Operation Theatre', 'OPD',
];
const STATUS_COLOR = { ONGOING: 'success', UPCOMING: 'info', COMPLETED: 'default' };
const today = () => localDate();
const emptyPosting = { classId: '', sectionId: '', department: '', hospital: '', startDate: '', endDate: '', requiredHours: 120, supervisorId: '', notes: '' };

export default function ClinicalManagement() {
  const [postings, setPostings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [filterClass, setFilterClass] = useState('');
  const [dlg, setDlg] = useState(null);
  const [roster, setRoster] = useState(null);      // { posting, date, students, edits }
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const notify = (message, severity = 'success') => setToast({ open: true, message, severity });
  const errorText = (e, fallback) => e?.response?.data?.message || fallback;

  const load = async () => {
    try {
      const [p, c, s, t] = await Promise.all([
        clinicalAPI.getPostings(filterClass ? { classId: filterClass } : {}),
        academicAPI.getClasses(), academicAPI.getSections(), teacherAPI.getAll(),
      ]);
      setPostings(p.data); setClasses(c.data); setSections(s.data); setFaculty(t.data);
    } catch (e) { notify(errorText(e, 'Could not load postings'), 'error'); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClass]);

  const sectionsOfClass = useMemo(
    () => sections.filter(s => (s.schoolClass?.id ?? s.classId) === Number(dlg?.classId)),
    [sections, dlg?.classId]
  );

  const save = async () => {
    const d = dlg;
    if (!d.classId || !d.department || !d.hospital || !d.startDate || !d.endDate || !d.requiredHours) {
      return notify('Program / year, department, hospital, dates and required hours are needed', 'error');
    }
    try {
      const body = { ...d, sectionId: d.sectionId || null, supervisorId: d.supervisorId || null };
      if (d.id) await clinicalAPI.updatePosting(d.id, body); else await clinicalAPI.createPosting(body);
      setDlg(null); notify('Posting saved'); load();
    } catch (e) { notify(errorText(e, 'Could not save posting'), 'error'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete the ${p.department} posting for ${p.className}? Recorded hours will be removed too.`)) return;
    try { await clinicalAPI.deletePosting(p.id); notify('Posting deleted'); load(); }
    catch (e) { notify(errorText(e, 'Could not delete posting'), 'error'); }
  };

  const openRoster = async (posting, date = today()) => {
    const inRange = date >= posting.startDate && date <= posting.endDate;
    const day = inRange ? date : posting.startDate;
    try {
      const res = await clinicalAPI.getRoster(posting.id, day);
      const edits = {};
      res.data.students.forEach(s => { edits[s.studentId] = { status: s.dayStatus || 'PRESENT', hours: s.dayHours ?? 6 }; });
      setRoster({ posting: res.data.posting, date: day, students: res.data.students, edits });
    } catch (e) { notify(errorText(e, 'Could not load the roster'), 'error'); }
  };

  const saveHours = async () => {
    const entries = roster.students.map(s => ({ studentId: s.studentId, status: roster.edits[s.studentId].status, hours: Number(roster.edits[s.studentId].hours) || 0 }));
    try {
      const res = await clinicalAPI.markHours({ postingId: roster.posting.id, date: roster.date, entries });
      notify(res.data.message || 'Hours saved');
      openRoster(roster.posting, roster.date);
      load();
    } catch (e) { notify(errorText(e, 'Could not save hours'), 'error'); }
  };

  const setEdit = (id, patch) => setRoster(r => ({ ...r, edits: { ...r.edits, [id]: { ...r.edits[id], ...patch } } }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ flex: 1 }}>Clinical Postings</Typography>
        <TextField select size="small" label="Program / Year" value={filterClass} onChange={e => setFilterClass(e.target.value)} sx={{ minWidth: 260 }}>
          <MenuItem value="">All programs</MenuItem>
          {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
        </TextField>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ ...emptyPosting })}>Schedule posting</Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow>
              <TableCell>Program / Year</TableCell><TableCell>Batch</TableCell><TableCell>Department</TableCell><TableCell>Hospital</TableCell>
              <TableCell>Period</TableCell><TableCell align="right">Hours</TableCell><TableCell>Supervisor</TableCell><TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {postings.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No clinical postings scheduled yet</TableCell></TableRow>}
              {postings.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{p.className}</TableCell>
                  <TableCell>{p.sectionName || 'All batches'}</TableCell>
                  <TableCell>{p.department}</TableCell>
                  <TableCell>{p.hospital}</TableCell>
                  <TableCell>{p.startDate} → {p.endDate}</TableCell>
                  <TableCell align="right">{p.requiredHours}</TableCell>
                  <TableCell>{p.supervisorName || '-'}</TableCell>
                  <TableCell><Chip size="small" label={p.status} color={STATUS_COLOR[p.status]} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Roster & clinical hours"><IconButton size="small" color="primary" onClick={() => openRoster(p)}><HoursIcon /></IconButton></Tooltip>
                    <IconButton size="small" onClick={() => setDlg({ ...p, classId: p.classId, sectionId: p.sectionId || '', supervisorId: p.supervisorId || '', notes: p.notes || '' })}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(p)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Posting dialog */}
      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="md" fullWidth>
        <DialogTitle>{dlg?.id ? 'Edit clinical posting' : 'Schedule clinical posting'}</DialogTitle>
        <DialogContent dividers>
          {dlg && (
            <Grid container spacing={2} sx={{ pt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth required label="Program / Year" value={dlg.classId} onChange={e => setDlg({ ...dlg, classId: e.target.value, sectionId: '' })}>
                  {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Batch (optional)" value={dlg.sectionId} disabled={!dlg.classId} onChange={e => setDlg({ ...dlg, sectionId: e.target.value })}>
                  <MenuItem value="">All batches</MenuItem>
                  {sectionsOfClass.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete freeSolo options={DEPARTMENTS} value={dlg.department} inputValue={dlg.department}
                  onInputChange={(e, v) => setDlg(d => ({ ...d, department: v }))}
                  renderInput={params => <TextField {...params} required label="Department / clinical area" />} />
              </Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth required label="Hospital / site" value={dlg.hospital} onChange={e => setDlg({ ...dlg, hospital: e.target.value })} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth required type="date" label="Start date" InputLabelProps={{ shrink: true }} value={dlg.startDate} onChange={e => setDlg({ ...dlg, startDate: e.target.value })} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth required type="date" label="End date" InputLabelProps={{ shrink: true }} value={dlg.endDate} onChange={e => setDlg({ ...dlg, endDate: e.target.value })} /></Grid>
              <Grid item xs={12} sm={4}><TextField fullWidth required type="number" label="Required hours" inputProps={{ min: 1 }} value={dlg.requiredHours} onChange={e => setDlg({ ...dlg, requiredHours: e.target.value })} /></Grid>
              <Grid item xs={12}>
                <TextField select fullWidth label="Supervising clinical instructor" value={dlg.supervisorId} onChange={e => setDlg({ ...dlg, supervisorId: e.target.value })}>
                  <MenuItem value="">Not assigned</MenuItem>
                  {faculty.map(t => <MenuItem key={t.id} value={t.id}>{t.firstName} {t.lastName} — {t.designation || 'Faculty'}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={2} label="Notes for students" value={dlg.notes} onChange={e => setDlg({ ...dlg, notes: e.target.value })} /></Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDlg(null)}>Cancel</Button><Button variant="contained" onClick={save}>Save posting</Button></DialogActions>
      </Dialog>

      {/* Roster + hours dialog */}
      <Dialog open={!!roster} onClose={() => setRoster(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          {roster?.posting.department} — {roster?.posting.className}{roster?.posting.sectionName ? ` (${roster.posting.sectionName})` : ''}
          <Typography variant="body2" color="text.secondary">{roster?.posting.hospital} · {roster?.posting.startDate} → {roster?.posting.endDate} · {roster?.posting.requiredHours} hours required</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {roster && (
            <>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <TextField type="date" size="small" label="Record hours for" InputLabelProps={{ shrink: true }}
                  inputProps={{ min: roster.posting.startDate, max: roster.posting.endDate }}
                  value={roster.date} onChange={e => openRoster(roster.posting, e.target.value)} />
                <Typography variant="caption" color="text.secondary">Set each student's status and hours for this day, then save.</Typography>
              </Box>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Student</TableCell><TableCell>Admission No</TableCell><TableCell sx={{ width: 190 }}>Completed</TableCell>
                  <TableCell sx={{ width: 130 }}>Status</TableCell><TableCell sx={{ width: 100 }}>Hours</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {roster.students.map(s => (
                    <TableRow key={s.studentId}>
                      <TableCell sx={{ fontWeight: 600 }}>{s.studentName}</TableCell>
                      <TableCell>{s.admissionNo}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={s.percent} color={s.percent >= 100 ? 'success' : 'primary'} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                          <Typography variant="caption" sx={{ minWidth: 70 }}>{s.hoursCompleted}h · {s.percent}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <TextField select size="small" fullWidth value={roster.edits[s.studentId].status} onChange={e => setEdit(s.studentId, { status: e.target.value, hours: e.target.value === 'PRESENT' ? (roster.edits[s.studentId].hours || 6) : 0 })}>
                          <MenuItem value="PRESENT">Present</MenuItem><MenuItem value="ABSENT">Absent</MenuItem><MenuItem value="LEAVE">Leave</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" inputProps={{ min: 0, max: 12, step: 0.5 }} disabled={roster.edits[s.studentId].status !== 'PRESENT'}
                          value={roster.edits[s.studentId].hours} onChange={e => setEdit(s.studentId, { hours: e.target.value })} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setRoster(null)}>Close</Button><Button variant="contained" onClick={saveHours}>Save hours</Button></DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
